/*
 * <zpen-plinko-popup>
 * Modal with a small plinko game. The visitor gets MAX_DROPS drops; one of them, picked at random, lands in
 * a prize slot (the others in an empty one), then the visitor enters an email to reveal the discount code. Shows after a delay, once per visitor for `data-hide-days`.
 */
(() => {
  if (customElements.get('zpen-plinko-popup')) return;

  const STORAGE_KEY = 'zpen-plinko-popup';
  const W = 320, H = 380;
  const BALL_R = 8, PEG_R = 5;
  const PEG_TOP = 70, PEG_DY = 38, PEG_ROWS = 7, PEG_DX = 46;
  const SLOTS = 7, SLOT_TOP = 330, FLOOR = 372;
  const MAX_DROPS = 3;
  const GRAVITY = 900, BOUNCE = 0.5, STEP = 1 / 240;

  const pegs = [];
  for (let r = 0; r < PEG_ROWS; r++) {
    const count = r % 2 ? SLOTS - 1 : SLOTS;
    const start = r % 2 ? PEG_DX : PEG_DX / 2; // odd rows are shifted by half a gap
    for (let i = 0; i < count; i++) pegs.push({ x: start + i * PEG_DX, y: PEG_TOP + r * PEG_DY });
  }
  const SLOT_W = W / SLOTS;

  const read = () => {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
    } catch (e) {
      return null;
    }
  };
  const write = () => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ t: Date.now() }));
    } catch (e) {}
  };

  class ZpenPlinkoPopup extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;

      this.dialog = this.querySelector('.zpp__dialog');
      this.canvas = this.querySelector('.zpp__canvas');
      this.playEl = this.querySelector('[data-zpp-play]');
      this.resultEl = this.querySelector('[data-zpp-result]');
      this.dropButton = this.querySelector('[data-zpp-drop]');
      this.copyButton = this.querySelector('[data-zpp-copy]');
      this.claimEl = this.querySelector('[data-zpp-claim]');
      this.revealEl = this.querySelector('[data-zpp-reveal]');
      this.formEl = this.querySelector('[data-zpp-form]');
      this.emailInput = this.querySelector('.zpp__input');
      this.submitButton = this.querySelector('[data-zpp-submit]');
      this.errorEl = this.querySelector('[data-zpp-error]');
      if (!this.dialog || !this.canvas) return;

      this.ctx = this.canvas.getContext('2d');
      this.labels = (this.dataset.labels || '').split('|').map((l) => l.trim());
      while (this.labels.length < SLOTS) this.labels.push('');
      // an empty slot means "drop again"; if every slot is empty they all win
      if (!this.labels.some(Boolean)) this.labels = this.labels.map(() => '5% OFF');
      this.missEl = this.querySelector('[data-zpp-miss]');
      this.dropLabel = this.dropButton.textContent.trim();
      this.missSlot = -1;
      this.prizeEl = this.querySelector('[data-zpp-prize]');
      this.hideDays = Number(this.dataset.hideDays) || 0;
      this.designMode = this.dataset.designMode === 'true';
      this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.ball = null;
      this.winSlot = -1;
      this.drops = 0;
      this.winDrop = 1 + Math.floor(Math.random() * MAX_DROPS);
      this.raf = 0;
      this.timer = 0;

      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      this.canvas.width = W * dpr;
      this.canvas.height = H * dpr;
      this.ctx.scale(dpr, dpr);
      this.style_ = getComputedStyle(this);
      this.draw();

      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-zpp-close]')) this.close();
      });
      // any [data-zpp-open] element on the page (e.g. the product page discount badge) opens the pop-up
      this.onOpenClick = (event) => {
        if (event.target.closest('[data-zpp-open]')) this.open();
      };
      document.addEventListener('click', this.onOpenClick);
      this.dropButton.addEventListener('click', () => this.drop());
      this.copyButton?.addEventListener('click', () => this.copy());
      this.formEl?.addEventListener('submit', (event) => this.subscribe(event));
      this.onKey = (event) => this.keydown(event);
      this.addEventListener('keydown', this.onKey);

      // theme editor: open while this section is selected
      this.onSelect = (event) => {
        if (event.target.contains(this)) this.open(true);
      };
      this.onDeselect = (event) => {
        if (event.target.contains(this)) this.close(true);
      };
      document.addEventListener('shopify:section:select', this.onSelect);
      document.addEventListener('shopify:section:deselect', this.onDeselect);

      if (this.designMode) {
        this.open(true);
        return;
      }
      const seen = read();
      if (this.hideDays > 0 && seen && Date.now() - seen.t < this.hideDays * 864e5) return;
      this.timer = setTimeout(() => this.open(), (Number(this.dataset.delay) || 0) * 1000);
    }

    disconnectedCallback() {
      this.ready = false;
      clearTimeout(this.timer);
      cancelAnimationFrame(this.raf);
      this.removeEventListener('keydown', this.onKey);
      document.removeEventListener('click', this.onOpenClick);
      document.removeEventListener('shopify:section:select', this.onSelect);
      document.removeEventListener('shopify:section:deselect', this.onDeselect);
      document.documentElement.style.overflow = '';
    }

    open(editor) {
      if (!this.hidden) return;
      this.hidden = false;
      this.opener = document.activeElement;
      document.documentElement.style.overflow = 'hidden';
      this.dialog.focus();
      if (!editor && this.hideDays > 0) write();
    }

    close(editor) {
      if (this.hidden) return;
      this.hidden = true;
      cancelAnimationFrame(this.raf);
      this.raf = 0;
      document.documentElement.style.overflow = '';
      if (!editor) this.opener?.focus?.();
      // fresh board next time
      this.ball = null;
      this.winSlot = -1;
      this.dropButton.disabled = false;
      this.dropButton.textContent = this.dropLabel;
      this.missSlot = -1;
      this.drops = 0;
      this.winDrop = 1 + Math.floor(Math.random() * MAX_DROPS);
      if (this.missEl) this.missEl.hidden = true;
      this.playEl.hidden = false;
      this.resultEl.hidden = true;
      if (this.claimEl) this.claimEl.hidden = false;
      if (this.revealEl) this.revealEl.hidden = true;
      if (this.errorEl) this.errorEl.hidden = true;
      if (this.submitButton) this.submitButton.disabled = false;
      this.draw();
    }

    keydown(event) {
      if (event.key === 'Escape') {
        this.close();
        return;
      }
      if (event.key !== 'Tab') return;
      const items = Array.from(this.dialog.querySelectorAll('button, a[href], input')).filter((el) => !el.disabled && el.offsetParent !== null);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === this.dialog)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    // saves the email as a newsletter subscriber through the store's customer form, then reveals the code
    async subscribe(event) {
      event.preventDefault();
      const email = this.emailInput.value.trim();
      if (!this.emailInput.checkValidity() || !email) {
        this.showError(true);
        this.emailInput.setAttribute('aria-invalid', 'true');
        this.emailInput.focus();
        return;
      }
      this.showError(false);
      this.emailInput.removeAttribute('aria-invalid');
      this.submitButton.disabled = true;
      try {
        await fetch(this.formEl.action, { method: 'POST', body: new FormData(this.formEl), headers: { Accept: 'text/html' } });
      } catch (e) {
        // the code is still revealed: a failed request should not cost the visitor their prize
      }
      this.claimEl.hidden = true;
      this.revealEl.hidden = false;
      this.revealEl.querySelector('a, button')?.focus();
    }

    showError(visible) {
      if (this.errorEl) this.errorEl.hidden = !visible;
    }

    copy() {
      const code = this.querySelector('[data-zpp-code]').textContent.trim();
      const done = () => {
        const original = this.copyButton.textContent;
        this.copyButton.textContent = this.copyButton.dataset.copied;
        setTimeout(() => (this.copyButton.textContent = original), 1800);
      };
      if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(code).then(done, done);
      } else {
        done();
      }
    }

    // ---- game ----
    drop() {
      if (this.ball) return;
      this.drops += 1;
      this.dropButton.disabled = true;
      this.missSlot = -1;
      this.winSlot = -1;
      if (this.missEl) this.missEl.hidden = true;
      this.target = this.pickTarget();
      if (this.reduced) {
        this.winSlot = this.target;
        this.ball = { x: (this.winSlot + 0.5) * SLOT_W, y: FLOOR - BALL_R, vx: 0, vy: 0, still: 0 };
        this.draw();
        this.finish();
        return;
      }
      // plain physics, no steering: find a drop that naturally ends in the target slot, then play it back
      this.path = this.plan(this.target);
      this.pathIndex = 0;
      this.ball = { x: this.path.xs[0], y: this.path.ys[0] };
      this.last = performance.now();
      this.acc = 0;
      this.raf = requestAnimationFrame((t) => this.frame(t));
    }

    // exactly one of the drops (picked at random) lands in a prize slot, the others in an empty one
    pickTarget() {
      const wantPrize = this.drops === this.winDrop;
      const matching = this.labels.map((l, i) => (Boolean(l) === wantPrize ? i : -1)).filter((i) => i >= 0);
      const pool = matching.length ? matching : this.labels.map((l, i) => i);
      return pool[Math.floor(Math.random() * pool.length)];
    }

    frame(now) {
      this.raf = 0;
      this.acc += Math.min(0.05, (now - this.last) / 1000);
      this.last = now;
      const lastIndex = this.path.xs.length - 1;
      while (this.acc >= STEP && this.pathIndex < lastIndex) {
        this.pathIndex += 1;
        this.acc -= STEP;
      }
      this.ball.x = this.path.xs[this.pathIndex];
      this.ball.y = this.path.ys[this.pathIndex];
      if (this.pathIndex >= lastIndex) {
        this.winSlot = this.path.slot;
        this.draw();
        this.finish();
        return;
      }
      this.draw();
      this.raf = requestAnimationFrame((t) => this.frame(t));
    }

    // simulates random drops until one comes to rest in `target`; falls back to the closest one found
    plan(target) {
      let best = null;
      for (let attempt = 0; attempt < 4000; attempt++) {
        const run = this.simulate(24 + Math.random() * (W - 48), (Math.random() - 0.5) * 20);
        if (!run) continue;
        if (run.slot === target) return run;
        if (!best || Math.abs(run.slot - target) < Math.abs(best.slot - target)) best = run;
      }
      return best || this.simulate(W / 2, 0) || { xs: [W / 2], ys: [FLOOR - BALL_R], slot: target };
    }

    simulate(x, vx) {
      const b = { x, y: 16, vx, vy: 0, still: 0 };
      const xs = [b.x];
      const ys = [b.y];
      for (let n = 0; n < 240 * 15; n++) {
        const slot = this.step(b, STEP);
        xs.push(b.x);
        ys.push(b.y);
        if (slot === -2) return null;
        if (slot >= 0) {
          for (let k = 0; k < 20; k++) {
            xs.push(b.x);
            ys.push(b.y);
          }
          return { xs, ys, slot };
        }
      }
      return null;
    }

    // one physics step; returns the slot the ball came to rest in, -2 if it got stuck, otherwise -1
    step(b, dt) {
      b.vy += GRAVITY * dt;
      b.x += b.vx * dt;
      b.y += b.vy * dt;

      for (const p of pegs) {
        const min = BALL_R + PEG_R;
        const dy = b.y - p.y;
        if (dy > min || dy < -min) continue;
        const dx = b.x - p.x;
        const d2 = dx * dx + dy * dy;
        if (d2 >= min * min) continue;
        const d = Math.sqrt(d2) || 0.001;
        const nx = dx / d;
        const ny = dy / d;
        b.x = p.x + nx * min;
        b.y = p.y + ny * min;
        const vn = b.vx * nx + b.vy * ny;
        if (vn < 0) {
          // bounce along the normal, with a little friction along the surface
          const tx = -ny;
          const ty = nx;
          const vt = (b.vx * tx + b.vy * ty) * 0.92;
          b.vx = -BOUNCE * vn * nx + vt * tx;
          b.vy = -BOUNCE * vn * ny + vt * ty;
        }
      }

      if (b.x < BALL_R) {
        b.x = BALL_R;
        b.vx = Math.abs(b.vx) * 0.5;
      } else if (b.x > W - BALL_R) {
        b.x = W - BALL_R;
        b.vx = -Math.abs(b.vx) * 0.5;
      }

      if (b.y + BALL_R > SLOT_TOP) {
        const i = Math.max(0, Math.min(SLOTS - 1, Math.floor(b.x / SLOT_W)));
        const lo = i * SLOT_W + BALL_R + 2;
        const hi = (i + 1) * SLOT_W - BALL_R - 2;
        if (b.x < lo) {
          b.x = lo;
          b.vx = Math.abs(b.vx) * 0.3;
        } else if (b.x > hi) {
          b.x = hi;
          b.vx = -Math.abs(b.vx) * 0.3;
        }
        if (b.y > FLOOR - BALL_R) {
          b.y = FLOOR - BALL_R;
          b.vy = -b.vy * 0.35;
          b.vx *= 0.8;
          if (Math.abs(b.vy) < 40) return i;
        }
      }

      // a ball balanced on a peg would look odd, so such drops are discarded
      if (Math.hypot(b.vx, b.vy) < 8 && b.y < SLOT_TOP) {
        b.still += dt;
        if (b.still > 0.3) return -2;
      } else {
        b.still = 0;
      }
      return -1;
    }

    miss() {
      this.missSlot = this.winSlot;
      this.winSlot = -1;
      this.ball = null;
      this.draw();
      if (this.missEl) {
        const left = MAX_DROPS - this.drops;
        const d = this.missEl.dataset;
        const note = left === 1 ? d.leftOne : (d.leftMany || '').replace('{count}', left);
        this.missEl.textContent = [d.miss, note].filter(Boolean).join(' ');
        this.missEl.hidden = false;
      }
      this.dropButton.textContent = this.dropButton.dataset.retry || this.dropLabel;
      this.dropButton.disabled = false;
      this.dropButton.focus();
    }

    finish() {
      setTimeout(() => {
        if (this.hidden) return;
        // only reached if no natural drop into the target slot was found
        if (this.winSlot !== this.target) {
          this.winSlot = this.target;
          this.ball.x = (this.winSlot + 0.5) * SLOT_W;
          this.draw();
        }
        if (!this.labels[this.winSlot]) {
          this.miss();
          return;
        }
        if (this.prizeEl) this.prizeEl.textContent = this.labels[this.winSlot] || this.prizeEl.dataset.default;
        this.playEl.hidden = true;
        this.resultEl.hidden = false;
        (this.emailInput || this.resultEl.querySelector('a, button'))?.focus();
        if (this.hideDays > 0) write();
      }, 900);
    }

    color(name, fallback) {
      return this.style_.getPropertyValue(name).trim() || fallback;
    }

    draw() {
      const c = this.ctx;
      const accent = this.color('--zpp-accent', '#c8f336');
      const accentText = this.color('--zpp-accent-text', '#121212');
      c.clearRect(0, 0, W, H);

      // slots
      c.textAlign = 'center';
      c.textBaseline = 'middle';
      c.font = '700 11px system-ui, sans-serif';
      for (let i = 0; i < SLOTS; i++) {
        const x = i * SLOT_W;
        const prize = Boolean(this.labels[i]);
        const hit = i === this.winSlot && prize;
        c.fillStyle = hit ? accent : prize ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.03)';
        c.fillRect(x + 2, SLOT_TOP, SLOT_W - 4, H - SLOT_TOP - 4);
        c.fillStyle = hit ? accentText : prize ? accent : 'rgba(255,255,255,0.35)';
        c.fillText(prize ? this.labels[i] : '\u2715', x + SLOT_W / 2, SLOT_TOP + (H - SLOT_TOP) / 2 - 2, SLOT_W - 6);
        if (!prize) {
          c.fillStyle = i === this.missSlot ? 'rgba(255,255,255,0.18)' : 'rgba(0,0,0,0)';
          c.fillRect(x + 2, SLOT_TOP, SLOT_W - 4, H - SLOT_TOP - 4);
        }
      }

      // pegs
      c.fillStyle = 'rgba(255,255,255,0.85)';
      for (const p of pegs) {
        c.beginPath();
        c.arc(p.x, p.y, PEG_R, 0, Math.PI * 2);
        c.fill();
      }

      // ball
      if (this.ball) {
        c.save();
        c.shadowColor = accent;
        c.shadowBlur = 14;
        c.fillStyle = accent;
        c.beginPath();
        c.arc(this.ball.x, this.ball.y, BALL_R, 0, Math.PI * 2);
        c.fill();
        c.restore();
      }
    }
  }

  customElements.define('zpen-plinko-popup', ZpenPlinkoPopup);
})();
